import { Inject, Injectable } from '@angular/core';
import { IDBProduct, OnlineProduct } from '../interfaces/product';
import { Category } from '../interfaces/category';
import { Meta, Title } from '@angular/platform-browser';
import { MetaTag } from '../interfaces/metatag';
import { DOCUMENT } from '@angular/common';
import { UserService } from './user.service';
import { Company } from '../interfaces/company';
import { BRAND, SITE_ORIGIN } from '../brand';
import { CurrencyService } from './currency.service';

// What the shop is, for any page that does not say what it is itself: a marketplace of verified sellers, delivering in
// all 64 districts (PrimeHat_Pages_Copy.md 1). The tagline, then the brand - the home page's title until its home.hero
// row gives one; index.html repeats it and SITE_DESCRIPTION.
export const SITE_TITLE = BRAND.tagline + ' | ' + BRAND.name;
// The entity sentence: identical wording wherever the shop describes itself, so search and AI engines take every
// mention for one business. It names no method of payment: none is promised before the checkout offers it.
export const ENTITY_SENTENCE = BRAND.name + ' is an online marketplace in Bangladesh where verified sellers list electronics, fashion, home and everyday products, with delivery across all 64 districts. Sellers run their shops on PrimeCommerce, ' + BRAND.name + '\'s seller platform.';
/// How a listing page's description says the shop takes payment, where it fits. It names no method (no cash on
/// delivery, bKash or Nagad until the checkout offers them): the checkout shows the ways to pay.
export const PAYMENT_SENTENCE = 'Ways to pay are shown at checkout.';
/// The site's own description (the home page's meta, without the payment methods the copy names in brackets: none is
/// promised before the checkout offers it). Under the 155 characters a search result shows.
export const SITE_DESCRIPTION = BRAND.name + ' is an online marketplace in Bangladesh: electronics, fashion, home and everyday products from verified sellers, delivery to all 64 districts.';

@Injectable({
    providedIn: 'root'
})
export class RootService {

    /// The product structured data last written, to write again in the shown currency when it changes - Locale/me's
    /// answer (taka) can arrive after the product, on the server render too.
    private productSchema: { product: IDBProduct, className: string, shipMap: any } = null;

    constructor(private title: Title, private userService:UserService,
        private meta: Meta, @Inject(DOCUMENT) private document: Document, private currency: CurrencyService) { 
        this.currency.changes$.subscribe(() => {
            const last = this.productSchema;
            if (last && this.document.head.getElementsByClassName(last.className).length > 0) {
                this.insertProductSchema(last.product, last.className, last.shipMap);
            }
        });
    }

    /// The currency structured data states its amounts in: the one prices are shown in (taka on PrimeHat), converted
    /// as every price on the page is (CurrencyService.convert) - so the page and its markup say the same.
    private get shownCurrency(): string {
        return this.currency.isLocal ? this.currency.options.code : this.currency.site.code;
    }

    home(): string {
        return '/';
    }

    shop(): string {
        return `/products`;
    }

    category(category: Partial<Category>): string {
        if (category.type === 'products') {
            const basePath = this.shop();

            if ('slug' in category) {
                return `${basePath}/${category.slug}`;
            }
            if ('id' in category) {
                return `${basePath}/${category.id}`;
            }

            throw Error('Provide category with "path", "slug" or "id".');
        }
        if (category.type === 'blog') {
            return this.blog(category.slug);
        }

        throw Error('Provided category with unknown type.');
    }

    product(product: Partial<OnlineProduct>): string {
        const basePath = '/products/details';

        // if ('Slug' in product) {
        //     return `${basePath}/${product.Slug}`;
        // }
        if ('Slug' in product) {
            return `${basePath}/${product.Slug}`;
        }

        throw Error('Provide product with "slug" or "id".');
    }

    productBySlug(slug): string {
        const basePath = '/products/details';

        return `${basePath}/${slug}`; 
    }

    categoryBySlug(categorySlug: string): string {
        const basePath = this.shop();

        return `${basePath}/${categorySlug}`;
    }

    brand(brand: string): string {
        return '/products/brand/' + brand;
    }

    brandWithCat(brand: string, cat: string): string {
        return '/products/brand/' + brand + "/" + cat;
    }

    cart(): string {
        return '/account/cart';
    }

    checkout(): string {
        if(this.userService.isLoggedIn()){
            return '/account/checkout';
        }
        else{
            return '/account/place-order';
        } 
    }

    wishlist(): string {
        return '/account/wishlist';
    }

    compare(): string {
        return '/account/compare';
    }

    blog(cat:string = ''): string {
        return '/blog'
    }

    /// A post's address: its own path where the list gave one ("/sell/stories/{slug}" for a story, "/bn/blog/{slug}" for
    /// a Bangla post), else the blog's.
    post(post: string | { slug: string, path?: string }): string {
        if (post && typeof post === 'object') {
            return post.path || `/blog/${post.slug}`;
        }
        return `/blog/${post}`;
    }

    login(): string {
        return '/account/login';
    }

    signup(): string {
        return '/account/signup';
    }

    terms(): string {
        return '/terms';
    }

    notFound(): string {
        return `/not-found`;
    }

    /// `exact`: the title is the whole <title> as it stands - a SitePage's, which carries the brand where its owner wants
    /// it ("How to Order on PrimeHat | Help") - and nothing is appended.
    setmeta(title: string, meta: MetaTag = null, exact = false) {
        if (title) {
            // Append the brand only when it is not already there - a title that ends with it
            // would otherwise carry it twice, on every page view and in every search result.
            const suffix = " | " + BRAND.name;
            const trimmed = title.trim();
            this.title.setTitle(exact || trimmed.toLowerCase().endsWith(suffix.toLowerCase()) ? trimmed : trimmed + suffix);
        }
        else {
            this.title.setTitle(SITE_TITLE);
        }

        if (meta && meta != null)
        {
            var des = meta.og_description;
            if (!des) {
                meta.og_description = des = SITE_DESCRIPTION;
            }
            // Cut at a word: this is the snippet a search result shows, and a plain
            // substring ended it mid-word ("Explore our curated collections t").
            this.meta.updateTag(
                {
                    name: 'description',
                    content: this.clip(des, 155)
                });

            // A page that passes no og_title of its own shares the title it already set, rather
            // than publishing the word "undefined" to everyone who shares the link.
            this.meta.updateTag(
                {
                    property: 'og:title',
                    content: meta.og_title ? meta.og_title : this.title.getTitle(),
                });

            this.meta.updateTag(
                {
                    property: 'og:description',
                    content: meta.og_description,
                });

            // Absolute: a seller's upload is stored as /filesystem/..., and a link preview cannot follow a relative address.
            const image = meta.og_image ? this.absoluteUrl(meta.og_image) : SITE_ORIGIN + '/assets/images/logos/primehat-share.png';

            this.meta.updateTag(
                {
                    property: 'og:image:secure_url',
                    content: image,
                });

            this.meta.updateTag(
                {
                    property: 'og:image',
                    content: image,
                });

            this.meta.updateTag(
                {
                    property: 'og:image:alt',
                    content: title,
                });

            this.meta.updateTag(
                {
                    property: 'og:type',
                    content: meta.og_type,
                });

            // og:site_name. It was written "og:sitename", which no reader recognises.
            this.meta.updateTag(
                {
                    property: 'og:site_name',
                    content: BRAND.name
                });

            this.meta.updateTag(
                {
                    property: 'og:url',
                    content: meta.og_url ? meta.og_url : this.publicUrl()
                });

            // Every image that reaches here clears the large card's 300x157 minimum: product
            // photos are 480px and up, and the banners, blog covers and about-page picture are
            // wider still. Brands pass their logo, which none has yet - a small logo would
            // want the summary card instead.
            this.setXCard('summary_large_image', meta.og_title || title, des, image, title);
        }
        else {
            // name, not property: property="description" is not a description tag, so these
            // pages went out with index.html's and a stray tag beside it.
            this.meta.updateTag(
                {
                    name: 'description',
                    content: this.clip(SITE_DESCRIPTION, 155)
                });

            this.meta.updateTag(
                {
                    property: 'og:description',
                    content: SITE_DESCRIPTION
                });

            if (title) {
                this.meta.updateTag(
                    {
                        property: 'og:title',
                        content: title,
                    });
            }
            else {
                this.meta.updateTag(
                    {
                        property: 'og:title',
                        content: SITE_TITLE,
                    });
            }

            this.meta.updateTag(
                {
                    property: 'og:image:secure_url',
                    content: SITE_ORIGIN + '/assets/images/logos/primehat-share.png',
                });

            this.meta.updateTag(
                {
                    property: 'og:image',
                    content: SITE_ORIGIN + '/assets/images/logos/primehat-share.png',
                });

            // The summary card, with the square icon: X crops a summary image to a square.
            this.setXCard('summary',
                title || BRAND.name,
                SITE_DESCRIPTION,
                SITE_ORIGIN + '/apple-touch-icon.png?v=2',
                BRAND.name);
        }
    }

    /** The shop's account on X, named on every card. TODO(operator): PrimeHat's, once it has one; empty, none is named. */
    private readonly xHandle = '';

    /**
     * The X (Twitter) card tags. X falls back to Open Graph for the title, description and
     * image, but not for twitter:card - without it a shared link shows no card at all, which
     * is what audits report as "X Cards not detected". The limits are X's: a title past 70
     * characters, a description past 200 or alt text past 420 is cut off in the card.
     *
     * summary_large_image needs an image of at least 300x157 and shows it at 2:1; summary
     * needs 144x144 and crops it square. JPG, PNG, WEBP and GIF, under 5 MB - never SVG.
     */
    private setXCard(card: 'summary' | 'summary_large_image', title: string, description: string, image: string, imageAlt: string) {
        this.meta.updateTag({ name: 'twitter:card', content: card });
        if (this.xHandle) {
            this.meta.updateTag({ name: 'twitter:site', content: this.xHandle });
        } else {
            this.meta.removeTag("name='twitter:site'");
        }
        this.meta.updateTag({ name: 'twitter:title', content: this.clip(title, 70) });
        this.meta.updateTag({ name: 'twitter:description', content: this.clip(description, 200) });
        this.meta.updateTag({ name: 'twitter:image', content: image });
        this.meta.updateTag({ name: 'twitter:image:alt', content: this.clip(imageAlt, 420) });
    }

    /** Whitespace collapsed, and cut at a word with an ellipsis when longer than max: a description the way setmeta writes it. */
    clip(text: string, max: number): string {
        const clean = (text || '').replace(/\s+/g, ' ').trim();
        if (clean.length <= max) {
            return clean;
        }

        let cut = clean.lastIndexOf(' ', max - 1);
        if (cut < max / 2) {
            // One long word: cut inside it, but never between the halves of a surrogate pair.
            cut = max - 1;
            const code = clean.charCodeAt(cut - 1);
            if (code >= 0xd800 && code <= 0xdbff) {
                cut--;
            }
        }

        return clean.substring(0, cut).replace(/[\s,;:.\-|]+$/, '') + '…';
    }

    removeStructuredData(className:string): void {
        try {
            const els = [];
            [className, 'structured-data-org'].forEach(c => {
                els.push(...Array.from(this.document.head.getElementsByClassName(c)));
            });
            els.forEach(el => this.document.head.removeChild(el));
        }
        catch { }
    }

    stripHtmlTags(input: string): string {
        return input.replace(/<\/?[^>]+(>|$)/g, '');
    }

    // ---------------------------------------------------------------------------------
    // Structured data (JSON-LD)
    //
    // What each page carries:
    //   every page   - WebSite and Organization (root.component)
    //   product page - Product and BreadcrumbList
    //   listing page - BreadcrumbList
    //   shop page    - Store (a seller's own page, every tab)
    //   blog post    - BlogPosting
    //   home page    - nothing more; it has no place in a hierarchy to describe
    //
    // REMOVED DELIBERATELY. Do not put these back without real data behind them:
    //   aggregateRating and review on Product. The rating fell back to 4.5 when a product
    //   had none, reviewCount was Math.random() - a different number on every render - and
    //   the review was written by the shop about itself. Google treats invented review
    //   markup as spam and can strip a site's rich results for it, and the stored ratings
    //   come from AI-generated reviews (CLAUDE.md, "Data you must not trust"). Mark ratings
    //   up again only from genuine customer reviews that are shown on the page.
    //   The four BreadcrumbLists on the home page - slogans pointing at category urls, not
    //   a trail the page sits on.
    //   FAQPage on every page - it described an FAQ that almost no page shows.
    // ---------------------------------------------------------------------------------

    private readonly siteOrigin = SITE_ORIGIN;

    /**
     * Returns exactly as the site states them, so the markup never promises more than the pages do: a 7-day return
     * window (FAQ, terms, refund policy), in Bangladesh, where the shop delivers. The buyer pays to send a change-of-mind
     * return back, as the FAQ and terms say, so returnFees says the customer does - the reading that never claims
     * something is free when it may not be. Change this with those pages.
     *
     * No shop-wide delivery statement: the delivery time and charge depend on the order and are shown before the buyer
     * pays, so there is no one rate or window to state. A product with delivery facts of its own gets them from
     * shippingFor; one without states none.
     */
    /// Out of stock, unless its seller sells a size ahead of its stock (product.SellsAhead): then a pre-order or a backorder,
    /// as the page says (ProductComponent.aheadLabel).
    private soldAhead(product: any, options: any[]): string
    {
        const ahead = product && product.SellsAhead;
        if (!ahead || product.ProductStatus === 6 || product.ProductStatus === 7) { return 'https://schema.org/OutOfStock'; }
        const sold = (options || []).filter(o => o && o.IsActive !== false && Number(ahead[o.ID]) > 0);
        if (!sold.length) { return 'https://schema.org/OutOfStock'; }
        return sold.some(o => o.PreorderUntil && new Date(o.PreorderUntil).getTime() > Date.now()) ? 'https://schema.org/PreOrder' : 'https://schema.org/BackOrder';
    }

    private get returnPolicy() { return {
        '@type': 'MerchantReturnPolicy',
        // The site's one country (the owner's Country and currency page); none named for a site that sells everywhere.
        applicableCountry: this.currency.site.country || undefined,
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 7,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/ReturnShippingFees',
    }; }

    private writeJsonLd(className: string, schema: object): void {
        let script = this.document.head.getElementsByClassName(className)[0] as HTMLScriptElement;

        if (!script) {
            script = this.document.createElement('script');
            script.setAttribute('class', className);
            this.document.head.appendChild(script);
        }

        script.type = 'application/ld+json';
        // The server render writes a script's text as it is, so a name holding "</script>" (a seller's shop, a product)
        // would end this one and start the next: "<", and the two line separators JSON allows and a script does not,
        // go escaped. Still the same JSON to anything that reads it.
        const slashU = String.fromCharCode(92) + 'u';
        script.text = JSON.stringify(schema)
            .replace(/</g, slashU + '003c')
            .replace(new RegExp(String.fromCharCode(0x2028), 'g'), slashU + '2028')
            .replace(new RegExp(String.fromCharCode(0x2029), 'g'), slashU + '2029');
    }

    private absoluteUrl(url: string): string {
        if (!url) return url;
        if (/^https?:\/\//i.test(url)) return url;
        return this.siteOrigin + (url.startsWith('/') ? url : '/' + url);
    }

    /** "desk-accessories" becomes "Desk Accessories" - only for rows with no stored name. */
    private titleFromSlug(slug: string): string {
        return (slug || '')
            .split('-')
            .filter(word => word)
            .map(word => word.charAt(0).toUpperCase() + word.substring(1))
            .join(' ');
    }

    /**
     * One OfferShippingDetails per country the product is known to deliver to, with that
     * country's real delivery window from the shop's stored delivery facts. This is for search
     * engines only: the page itself shows a shopper their own country and no list of others -
     * the owner's decision, so do not put the table back. Capped at twenty countries; without
     * stored facts none is stated (there is no shop-wide statement - see returnPolicy above).
     *
     * handlingTime is the shop's own (payment to the order being placed with the supplier) and
     * transitTime the supplier's window, and every day of the week counts: Google reads both as
     * BUSINESS days, Monday to Saturday, unless told otherwise, and the supplier's windows and the
     * dates on the page are calendar days. Today + handling + transit is exactly the page's dates.
     */
    private shippingFor(shipMap: any): any {
        const rows = ((shipMap && shipMap.Countries) || [])
            .filter((c: any) => c && c.State === 'ships' && c.MaxDays > 0 && /^[A-Z]{2}$/.test(c.Code || ''))
            .slice(0, 20);

        if (rows.length === 0) {
            return undefined;
        }

        // Each country's own rate where it has one, else the product's default (the US rate).
        // What a buyer there pays ON TOP of the listed price: the delivery charge - or, where a
        // small fee is shown as free delivery, the surcharge the price carries instead. The
        // listed price is the product's own, the same for every country.
        const fallback = (Number(shipMap && shipMap.DefaultFee) || 0) + (Number(shipMap && shipMap.DefaultSurcharge) || 0);
        const rateOf = (c: any) => c.Fee !== null && c.Fee !== undefined ? (Number(c.Fee) || 0) + (Number(c.Surcharge) || 0) : fallback;
        const handlingMin = Math.max(0, Number(shipMap && shipMap.HandlingMinDays) || 0);
        const handlingMax = Math.max(handlingMin, Number(shipMap && shipMap.HandlingMaxDays) || 0);

        return rows.map((c: any) => ({
            '@type': 'OfferShippingDetails',
            shippingRate: { '@type': 'MonetaryAmount', value: this.currency.convert(rateOf(c)), currency: this.shownCurrency },
            shippingDestination: { '@type': 'DefinedRegion', addressCountry: c.Code },
            deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: { '@type': 'QuantitativeValue', minValue: handlingMin, maxValue: handlingMax, unitCode: 'DAY' },
                transitTime: { '@type': 'QuantitativeValue', minValue: c.MinDays > 0 && c.MinDays < c.MaxDays ? c.MinDays : c.MaxDays, maxValue: c.MaxDays, unitCode: 'DAY' },
                businessDays: {
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: [
                        'https://schema.org/Monday', 'https://schema.org/Tuesday', 'https://schema.org/Wednesday',
                        'https://schema.org/Thursday', 'https://schema.org/Friday', 'https://schema.org/Saturday',
                        'https://schema.org/Sunday',
                    ],
                },
            },
        }));
    }

    insertProductSchema(product: IDBProduct, className = 'structured-data-product', shipMap: any = null): void {
        try {
            if (!product || !product.Name || !product.Slug) {
                this.productSchema = null;
                this.removeStructuredData(className);
                return;
            }
            this.productSchema = { product, className, shipMap };

            const url = `${this.siteOrigin}/products/details/${product.Slug}`;

            // ShortDescription can be null. It used to be read with .length inside a bare
            // catch, so a product without one silently got no Product markup at all.
            const description = this.stripHtmlTags(product.ShortDescription || product.Description || '').trim();

            const schema: any = {
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: product.Name,
                url: url,
            };

            if (product.Image) schema.image = [this.absoluteUrl(product.Image)];
            if (description) schema.description = description.length > 500 ? description.substring(0, 500) : description;
            if (product.SKU) schema.sku = product.SKU;
            if (product.BrandName) schema.brand = { '@type': 'Brand', name: product.BrandName };

            // Google does not list an offer whose price is not above zero.
            if (product.Price > 0) {
                // Stock is shown per variant on the page, so the offer follows the variants:
                // in stock while any of them is. It was hardcoded InStock before.
                // A product taken off the storefront for lack of stock (ProductStatus 6) is out of
                // stock whatever a variant's own count says: its page is still served, and says so.
                // The page's own rule (ProductComponent.soldOut): the sizes it shows (OptionList), switched
                // on and with something left - so the page and this never disagree. The Google feed
                // (MerchantFeedMapper) reads the same.
                const shown = [];
                (product.OptionList || []).forEach((group: any) => ((group && group.items) || []).forEach(item => item && shown.push(item)));
                const options = shown.length > 0 ? shown : (product.Options || []);
                // Taken offline by the owner (7) sells nothing either.
                const inStock = product.ProductStatus !== 6 && product.ProductStatus !== 7 && (options.length > 0
                    ? options.some(option => option && option.Quantity > 0 && option.IsActive !== false)
                    : product.AvailableQuantity > 0);

                // No priceValidUntil. It was fixed at 2024-12-31, and a date in the past can
                // keep the listing out of results entirely; these prices have no end date.
                schema.offers = {
                    '@type': 'Offer',
                    url: url,
                    price: this.currency.convert(product.Price),
                    priceCurrency: this.shownCurrency,
                    availability: inStock ? 'https://schema.org/InStock' : this.soldAhead(product, options),
                    itemCondition: 'https://schema.org/NewCondition',
                    shippingDetails: this.shippingFor(shipMap),
                    hasMerchantReturnPolicy: this.returnPolicy,
                };
                // Who sells it, as the page's "Sold by" says (CompanyIdentity_Spec.md 3.9): the shop's own products the
                // company's node, an independent seller's their own shop with its page (product.Seller - ShopPages on the
                // server). Never the company's @id for a seller's product: that node is the platform.
                if (product.SID === 786) {
                    schema.offers.seller = { '@type': 'Organization', '@id': this.organizationId, name: BRAND.name };
                }
                else if (product.Seller && product.Seller.Name) {
                    const seller: any = { '@type': 'Organization', name: product.Seller.Name };
                    if (product.Seller.Url) {
                        seller.url = this.absoluteUrl(product.Seller.Url);
                        seller['@id'] = seller.url + '#shop';
                    }
                    schema.offers.seller = seller;
                }
            }

            this.writeJsonLd(className, schema);
        }
        catch {
            this.removeStructuredData(className);
        }
    }

    /**
     * The trail the product page shows: the primary category's steps, then the product.
     * Built from the product rather than from the page's own breadcrumb array because that
     * array labels the last crumb "{name} Buy Online, Best Price", and a breadcrumb name is
     * the page's name, not a keyword line.
     */
    insertProductBreadcrumbSchema(product: IDBProduct, className = 'schema-breadcrumb'): void {
        if (!product || !product.Slug) {
            this.removeStructuredData(className);
            return;
        }

        const crumbs = [{ name: 'Home', url: this.siteOrigin + '/' }];
        const categories = product.Categories || [];
        const primary = categories.filter(c => c.IsPrimary)[0] || categories[0];

        if (primary && primary.Trail && primary.Trail.length > 0) {
            primary.Trail.forEach(step => crumbs.push({ name: step.Name, url: `${this.siteOrigin}/products/${step.Slug}` }));
        }
        else if (product.CategoryTree) {
            product.CategoryTree.split('>')
                .map(slug => slug.trim())
                .filter(slug => slug)
                .forEach(slug => crumbs.push({ name: this.titleFromSlug(slug), url: `${this.siteOrigin}/products/${slug}` }));
        }

        crumbs.push({ name: product.Name, url: `${this.siteOrigin}/products/details/${product.Slug}` });
        this.insertBreadcrumbSchema(crumbs, className);
    }

    /** The home page has no trail; this clears what a previous page left behind. */
    insertHomeSchema(): void {
        this.removeStructuredData('schema-breadcrumb');
        this.removeStructuredData('structured-data-product');
    }

    /**
     * Listing pages: the products root, a category, a brand, or a brand within a category.
     * Brand urls are /products/brand/{slug}. They were written as /products/{slug} before,
     * which is a CATEGORY route, and the names were keyword lines such as "{brand} Buy
     * Online, Easy, Top Quality And Free Shipping".
     */
    insertListPageSchema(brand: { name?: string, slug?: string } | null, category: Category | null, className = 'schema-breadcrumb'): void {
        // A client-side navigation from a product keeps that product's markup otherwise.
        this.removeStructuredData('structured-data-product');

        const crumbs = [
            { name: 'Home', url: this.siteOrigin + '/' },
            { name: 'Products', url: this.siteOrigin + '/products' },
        ];

        if (brand && brand.slug) {
            const brandUrl = `${this.siteOrigin}/products/brand/${brand.slug}`;
            crumbs.push({ name: brand.name || this.titleFromSlug(brand.slug), url: brandUrl });

            if (category && category.slug) {
                crumbs.push({ name: category.name || this.titleFromSlug(category.slug), url: `${brandUrl}/${category.slug}` });
            }
        }
        else if (category && category.slug) {
            (category.parents || [])
                .filter(parent => parent && parent.slug)
                .forEach(parent => crumbs.push({ name: parent.name || this.titleFromSlug(parent.slug), url: `${this.siteOrigin}/products/${parent.slug}` }));

            crumbs.push({ name: category.name || this.titleFromSlug(category.slug), url: `${this.siteOrigin}/products/${category.slug}` });
        }

        this.insertBreadcrumbSchema(crumbs, className);
    }

    /**
     * A seller's shop page (/shop/{slug} and its tabs): the shop as a Store - its name, its page, its logo and picture, its
     * own words, where it is, the profiles it published, and a rating only where the server says enough real reviews
     * stand behind it (RatingInMarkup: ShopFrontOptions.RatingMinReviews). Its @id is the one a product's offer names as
     * its seller (insertProductSchema), so search engines read the two as one shop. `description`: the page's own, in its
     * language; else the shop's tagline or about. The shop page removes it when it is left (removeShopPageSchema).
     */
    insertShopPageSchema(shop: any, description: string = null, className = 'schema-shop'): void {
        // A client-side navigation from a product, a listing or a post keeps their markup otherwise.
        ['structured-data-product', 'schema-breadcrumb', 'structured-data-blog', 'schema-faq', 'schema-webpage']
            .filter(name => name !== className)
            .forEach(name => this.removeStructuredData(name));

        if (!shop || !shop.Name || !shop.Url) {
            this.removeStructuredData(className);
            return;
        }

        const url = this.absoluteUrl(shop.Url);
        const node: any = {
            '@context': 'https://schema.org',
            '@type': 'Store',
            '@id': url + '#shop',
            name: shop.Name,
            url: url,
        };

        if (shop.Logo) node.logo = this.absoluteUrl(shop.Logo);
        const image = shop.OgImage || shop.Cover || shop.Logo;
        if (image) node.image = this.absoluteUrl(image);
        const words = this.clip(description || shop.Tagline || shop.About || '', 300);
        if (words) node.description = words;
        if (shop.Location || shop.CountryCode) {
            node.address = {
                '@type': 'PostalAddress',
                addressLocality: shop.Location || undefined,
                addressCountry: shop.CountryCode || undefined,
            };
        }
        // Only the links the seller chose to publish, and only web addresses: never a phone or a mailbox.
        const sameAs = [shop.Website, shop.Facebook, shop.Youtube].filter(link => /^https?:\/\//i.test(link || ''));
        if (sameAs.length) node.sameAs = sameAs;

        const stats = shop.Stats;
        if (shop.RatingInMarkup && stats && stats.RatingCount > 0 && stats.RatingAvg > 0) {
            node.aggregateRating = {
                '@type': 'AggregateRating',
                ratingValue: Math.round(stats.RatingAvg * 10) / 10,
                reviewCount: stats.RatingCount,
                bestRating: 5,
                worstRating: 1,
            };
        }

        this.writeJsonLd(className, node);
    }

    /** Takes the shop page's Store node away again: the next page is not the shop. */
    removeShopPageSchema(className = 'schema-shop'): void {
        this.removeStructuredData(className);
    }

    /// A site page's trail (siteTrail: Home, the pages above it, the page) as a BreadcrumbList, each at its full address.
    insertTrailSchema(crumbs: { label: string, url: string }[]): void {
        this.insertBreadcrumbSchema((crumbs || []).map(crumb => ({ name: crumb.label, url: crumb.url ? this.absoluteUrl(crumb.url) : '' })), 'schema-breadcrumb');
    }

    private insertBreadcrumbSchema(crumbs: { name: string, url: string }[], className: string): void {
        const items = crumbs.filter(crumb => crumb && crumb.name && crumb.url);

        if (items.length < 2) {
            this.removeStructuredData(className);
            return;
        }

        this.writeJsonLd(className, {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items.map((crumb, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: crumb.name,
                item: crumb.url,
            })),
        });
    }

    /**
     * A blog post: BlogPosting, from what Blog/GetBlog returns - which is also what the page
     * shows, so the markup states the headline, picture and dates a reader sees. The dates are
     * the post's own CreateDate and LastUpdated, given a zone (the database keeps them in UTC
     * without one). The shop writes the posts, so the author is the shop unless a post names
     * someone else. The publisher is the organisation node, by its @id. With it, the trail the
     * page shows (Home, Blog, the post) as a BreadcrumbList.
     */
    insertBlogPostingSchema(post: {
        title?: string, slug?: string, image?: string, author?: string, shortDescription?: string,
        published?: string, modified?: string, date?: string,
        path?: string, metaDescription?: string, language?: string, keywords?: string,
    }, className = 'structured-data-blog'): void {
        try {
            // A client-side navigation from a product keeps that product's markup otherwise.
            this.removeStructuredData('structured-data-product');
            this.removeStructuredData('schema-breadcrumb');

            if (!post || !post.title || !post.slug) {
                this.removeStructuredData(className);
                return;
            }

            const url = this.absoluteUrl(post.path || `/blog/${post.slug}`);
            const shop = { '@type': 'Organization', '@id': this.organizationId, name: BRAND.name, url: this.siteOrigin + '/' };
            const author = (post.author || '').trim();

            const schema: any = {
                '@context': 'https://schema.org',
                '@type': 'BlogPosting',
                headline: post.title,
                url: url,
                mainEntityOfPage: { '@type': 'WebPage', '@id': url },
                author: !author || author.toLowerCase() === BRAND.name.toLowerCase() ? shop : { '@type': 'Person', name: author },
                publisher: { '@id': this.organizationId },
                inLanguage: post.language === 'bn' ? 'bn' : 'en',
            };

            if (post.image) schema.image = [this.absoluteUrl(post.image)];

            const published = this.isoDate(post.published || post.date);
            const modified = this.isoDate(post.modified || post.date) || published;
            if (published) schema.datePublished = published;
            if (modified) schema.dateModified = modified;

            const description = this.stripHtmlTags(post.metaDescription || post.shortDescription || '').trim();
            if (description) schema.description = description;

            const keywords = String(post.keywords || '').split(',').map(word => word.trim()).filter(word => word);
            if (keywords.length) schema.keywords = keywords.join(', ');

            this.writeJsonLd(className, schema);

            this.insertBreadcrumbSchema([
                { name: 'Home', url: this.absoluteUrl(post.language === 'bn' ? '/bn' : '/') },
                { name: 'Blog', url: this.absoluteUrl(post.language === 'bn' ? '/bn/blog' : '/blog') },
                { name: post.title, url: url },
            ], 'schema-breadcrumb');
        }
        catch {
            this.removeStructuredData(className);
        }
    }

    /// The page's own node - AboutPage, ContactPage, CollectionPage or WebPage, as content/primehat/routes.md names them - in
    /// its language, published by the organisation node. One per page: the next page's replaces it.
    insertWebPageSchema(type: string, url: string, name: string, description: string, language = 'en', className = 'schema-webpage'): void {
        this.writeJsonLd(className, {
            '@context': 'https://schema.org',
            '@type': type,
            '@id': url + '#webpage',
            url: url,
            name: name,
            description: description || undefined,
            inLanguage: language || 'en',
            isPartOf: { '@type': 'WebSite', url: this.siteOrigin + '/' },
            publisher: { '@id': this.organizationId },
        });
    }

    /**
     * FAQPage, for a page that shows a FAQ block - exactly the questions and answers it shows, the answers as plain text.
     * Only there: the old markup put an FAQPage on every page, describing an FAQ almost none of them had.
     */
    insertFaqSchema(faq: { q: string, text: string }[], className = 'schema-faq'): void {
        const items = (faq || []).filter(item => item && item.q && item.text);
        if (items.length === 0) {
            this.removeStructuredData(className);
            return;
        }
        this.writeJsonLd(className, {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map(item => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.text },
            })),
        });
    }

    /**
     * The seller platform's page: SoftwareApplication with one Offer per plan (the server's Plans config, through
     * SitePages/Facts), in taka. A price only where the plan has one - "Custom" states none rather than a made-up zero.
     */
    insertSoftwareApplicationSchema(app: { name: string, url: string, description: string },
        plans: { name: string, priceBdt: number | null, priceFrom?: boolean }[], className = 'schema-software'): void {
        // One Offer per plan, a month's price in taka; a price that is where it starts (Enterprise) as its minimum.
        const offers = (plans || []).filter(plan => plan && plan.name && plan.priceBdt !== null && plan.priceBdt !== undefined).map(plan => {
            const offer: any = {
                '@type': 'Offer',
                name: plan.name,
                priceCurrency: 'BDT',
                url: this.absoluteUrl('/primecommerce/pricing'),
                seller: { '@id': this.organizationId },
            };
            if (plan.priceFrom) {
                offer.priceSpecification = { '@type': 'UnitPriceSpecification', minPrice: plan.priceBdt, priceCurrency: 'BDT', unitCode: 'MON' };
            } else {
                offer.price = plan.priceBdt;
                if (plan.priceBdt > 0) {
                    offer.priceSpecification = { '@type': 'UnitPriceSpecification', price: plan.priceBdt, priceCurrency: 'BDT', unitCode: 'MON' };
                }
            }
            return offer;
        });

        const schema: any = {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: app.name,
            url: this.absoluteUrl(app.url),
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            provider: { '@id': this.organizationId },
            publisher: { '@id': this.organizationId },
        };
        if (app.description) schema.description = app.description;
        if (offers.length) schema.offers = offers;
        this.writeJsonLd(className, schema);
    }

    /**
     * hreflang: one alternate link per language version of the page, and x-default on the English one - only when there
     * is more than one version (a page alone in its language has nothing to point to). Each call replaces the last
     * page's; an empty list removes them.
     */
    setHreflang(translations: { language: string, path: string }[]): void {
        Array.from(this.document.head.getElementsByClassName('hreflang-link')).forEach(el => this.document.head.removeChild(el));

        const versions = (translations || []).filter(t => t && t.language && t.path);
        if (versions.length < 2) {
            return;
        }
        const english = versions.filter(t => t.language === 'en')[0];
        const links = versions.map(t => ({ lang: t.language, href: this.absoluteUrl(t.path) }));
        if (english) {
            links.push({ lang: 'x-default', href: this.absoluteUrl(english.path) });
        }
        links.forEach(item => {
            const link = this.document.createElement('link');
            link.setAttribute('rel', 'alternate');
            link.setAttribute('hreflang', item.lang);
            link.setAttribute('href', item.href);
            link.setAttribute('class', 'hreflang-link');
            this.document.head.appendChild(link);
        });
    }

    /** A date the API sent without a zone, as ISO 8601 in UTC; a date alone stays a date. */
    private isoDate(value: string): string {
        const text = String(value || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
        if (text.length === 10) return text;
        return /(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : text + 'Z';
    }

    /**
     * WebSite with a name is what Google reads for the site name shown in results. The
     * SearchAction no longer produces a search box - Google retired that in November 2024 -
     * but it is valid and harmless, and it documents the search url.
     */
    insertWebsiteSchema(className = 'schema-web-search'): void {
        this.writeJsonLd(className, {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: BRAND.name,
            url: this.siteOrigin + '/',
            publisher: { '@id': this.organizationId },
            potentialAction: {
                '@type': 'SearchAction',
                target: {
                    '@type': 'EntryPoint',
                    urlTemplate: `${this.siteOrigin}/products?q={search_term_string}`,
                },
                'query-input': 'required name=search_term_string',
            },
        });
    }

    /// The one organisation node every other node points to by its @id: the brand, the company
    /// behind it and how to reach it, all from the Company section (CompanyIdentity_Spec.md 3.14).
    /// OnlineStore is the subtype Google suggests for a shop; legalName is for exactly this case - a
    /// registered name that is not the brand. It replaced a bare Organization with no legal name.
    /// Where it sells (Bangladesh), in what (taka), and how it is paid: the methods the checkout
    /// offers every shopper today (SitePages/Facts) - none named while it offers none.
    insertOrganizationSchema(c: Company, facts: { paymentMethods?: string[] } = null, className = 'schema-organization'): void {
        if (!c) {
            return;
        }
        const node: any = {
            '@context': 'https://schema.org',
            '@type': 'OnlineStore',
            '@id': this.organizationId,
            name: c.brandName || BRAND.name,
            // Only what the Company section states: an empty legal name or address is left out, never guessed.
            legalName: c.legalName || undefined,
            description: ENTITY_SENTENCE,
            url: this.siteOrigin + '/',
            areaServed: 'BD',
            currenciesAccepted: 'BDT',
            address: c.address && (c.address.street || c.address.city) ? {
                '@type': 'PostalAddress',
                streetAddress: [c.address.street, c.address.suite].filter(Boolean).join(', '),
                addressLocality: c.address.city,
                addressRegion: c.address.region,
                postalCode: c.address.postalCode,
                addressCountry: c.address.countryCode,
            } : undefined,
            // The contact page, not an address: the shop publishes none (the owner, 2026-09-27), and this block
            // is in every page's HTML, where an address is harvested for spam.
            contactPoint: [{
                '@type': 'ContactPoint',
                contactType: 'customer support',
                url: this.siteOrigin + '/contact',
                availableLanguage: ['English'],
            }],
            sameAs: c.socialProfiles && c.socialProfiles.length ? c.socialProfiles : undefined,
            hasMerchantReturnPolicy: this.returnPolicy,
        };
        // Google needs a logo it can fetch, at least 112px square.
        if (c.logoUrl) node.logo = c.logoUrl;
        if (c.phone) { node.telephone = c.phone; node.contactPoint[0].telephone = c.phone; }
        if (c.formationDate) node.foundingDate = c.formationDate;
        const methods = ((facts && facts.paymentMethods) || []).filter(method => method && method.trim());
        if (methods.length) node.paymentAccepted = methods.join(', ');
        this.writeJsonLd(className, node);
    }

    /// What every node that names the shop points to.
    private get organizationId(): string { return this.siteOrigin + '/#organization'; }

    /// This page's address on the public site. document.URL cannot stand in for it: on the server
    /// render it is where the web host proxied the request - http://127.0.0.1:4120/... - and that
    /// went out as the canonical of every product and category page, telling search engines the
    /// pages live on a local port they cannot reach, and as og:url wherever a page gave none.
    /// Tracking parameters are dropped, so a shared link is not a second address for the page.
    publicUrl(): string
    {
        const stripped = this.document.URL.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '').split('#')[0];
        const [path, query] = (stripped.startsWith('/') ? stripped : '/').split('?');
        const kept = (query || '').split('&')
            .filter(pair => pair && !/^(utm_[a-z0-9_]*|fbclid|gclid|gbraid|wbraid|msclkid|yclid|mc_cid|mc_eid)=/i.test(pair));

        return this.siteOrigin + path + (kept.length > 0 ? '?' + kept.join('&') : '');
    }

    createLinkForCanonicalURL(url?:string)
    {
        const canURL = (url == undefined ? this.publicUrl() : url).toLowerCase();
        let link; 
        let shouldAppend = false;
        if (this.document.head.getElementsByClassName('canon-link').length) {
            link = this.document.head.getElementsByClassName('canon-link')[0];
        } else {
            link = this.document.createElement('link');
            shouldAppend = true;
        }

        link.setAttribute('rel', 'canonical');
        link.setAttribute('class', 'canon-link');
        link.setAttribute('href', canURL);
        if(shouldAppend){ 
            this.document.head.appendChild(link);
        }  
    }

    getPriceValidUntil(): string {
        try{
            const currentDate = new Date(); 
            currentDate.setMonth(currentDate.getMonth() + 3);  
    
            const year = currentDate.getFullYear();
            const month = (currentDate.getMonth() + 1).toString().padStart(2, '0'); 
            const day = currentDate.getDate().toString().padStart(2, '0');
        
            return `${year}-${month}-${day}`;
        }
        catch{
            const currentDate = new Date(); 
            currentDate.setMonth(currentDate.getMonth() + 3);  
            return null;
        } 
    }
}
